import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { History, Search, FileDown, RefreshCw } from "lucide-react";
import { INSTALLATION_TYPES, installationMeta } from "../lib/jobTypes";

/**
 * Quote History — flat, searchable table of every invoiced installation.
 * Each row is a (vehicle × installation) with its recorded price and
 * materials used. Meant as a manual reference AND as the source of pricing
 * anchors for the AI quoting agent when the "reference past jobs" toggle
 * is on in the Skill modal.
 *
 * Backend: GET /api/quote-agent/pricing-history?q&install&limit
 * Column set: Vehicle · Installation · Materials · Amount · Invoiced date
 */
export default function QuoteHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [install, setInstall] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (install) params.install = install;
      const { data } = await api.get("/quote-agent/pricing-history", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load pricing history");
    } finally { setLoading(false); }
  };

  // Single debounced effect — the [q, install] deps also fire on mount, so
  // we don't need a separate mount-only load(). This avoids the duplicate
  // GET /pricing-history requests observed under React StrictMode.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [q, install]);

  const stats = useMemo(() => {
    const byInstall = {};
    for (const r of rows) {
      const key = r.installation_type || "unknown";
      if (!byInstall[key]) byInstall[key] = { count: 0, sum: 0, min: Infinity, max: 0 };
      byInstall[key].count += 1;
      byInstall[key].sum += Number(r.price) || 0;
      byInstall[key].min = Math.min(byInstall[key].min, Number(r.price) || 0);
      byInstall[key].max = Math.max(byInstall[key].max, Number(r.price) || 0);
    }
    return byInstall;
  }, [rows]);

  const exportCsv = () => {
    const header = ["Vehicle", "Installation", "Condition", "Materials", "Wrap areas", "PPF areas", "PPF brand", "Price", "Customer", "Invoiced"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push([
        r.vehicle, installationMeta(r.installation_type)?.label || r.installation_type,
        r.condition, (r.materials || []).join(" | "),
        (r.wrap_areas || []).join(" | "), (r.ppf_areas || []).join(" | "),
        r.ppf_brand, r.price, r.customer, r.invoiced_at,
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wrapconsole-pricing-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="glass rounded-3xl p-6 space-y-4" data-testid="quote-history-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-white">
            <History className="w-4 h-4 text-slate-300" />
            <h3 className="font-heading text-lg">Quote history</h3>
            <span className="pill pill-muted text-[10px]">{total} invoiced installations</span>
          </div>
          <p className="text-xs text-slate-400 mt-1 max-w-lg">
            Every job that reached the invoice stage is broken out here — one row per vehicle × installation with the materials used and the amount charged. This list also feeds the AI quoting agent when the <b>Reference past finished jobs</b> toggle is on.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="quote-history-refresh"
            onClick={load}
            disabled={loading}
            className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-slate-200 flex items-center gap-1"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {rows.length > 0 && (
            <button
              data-testid="quote-history-export"
              onClick={exportCsv}
              className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-slate-200 flex items-center gap-1"
            >
              <FileDown className="w-3 h-3" /> CSV
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            data-testid="quote-history-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vehicle, customer, material…"
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <select
          data-testid="quote-history-install-filter"
          value={install}
          onChange={(e) => setInstall(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
        >
          <option value="" className="bg-neutral-900">All installations</option>
          {INSTALLATION_TYPES.map((t) => (
            <option key={t.value} value={t.value} className="bg-neutral-900">{t.label}</option>
          ))}
        </select>
      </div>

      {/* Quick stats per installation type */}
      {Object.keys(stats).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="quote-history-stats">
          {Object.entries(stats).sort((a, b) => b[1].count - a[1].count).map(([type, s]) => {
            const avg = s.sum / s.count;
            const label = installationMeta(type)?.label || type;
            return (
              <span key={type} className="pill pill-muted text-[10px]">
                {label} · {s.count} × avg ${Math.round(avg).toLocaleString()}
              </span>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto max-h-[520px] overflow-y-auto rounded-2xl border border-white/5">
        <table className="w-full text-sm">
          <thead className="text-left">
            <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5">
              <th className="px-4 py-3">Vehicle</th>
              <th className="px-4 py-3">Installation</th>
              <th className="px-4 py-3">Materials</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3">Invoiced</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-xs">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-xs italic">
                  No invoiced jobs yet. Once a quote is invoiced, its vehicles &amp; installations appear here for future reference.
                </td>
              </tr>
            )}
            {!loading && rows.map((r, idx) => {
              const meta = installationMeta(r.installation_type);
              return (
                <tr
                  key={`${r.invoice_id}-${idx}`}
                  data-testid={`quote-history-row-${idx}`}
                  className="border-b border-white/[0.03] hover:bg-white/[0.03] align-top"
                >
                  <td className="px-4 py-3 text-white">
                    <div>{r.vehicle || <span className="text-slate-500 italic">—</span>}</div>
                    {r.condition && <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{r.condition}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-200">
                    <div>{meta?.label || r.installation_type || <span className="text-slate-500 italic">—</span>}</div>
                    {r.wrap_areas?.length > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{r.wrap_areas.join(", ")}</div>
                    )}
                    {r.ppf_areas?.length > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{r.ppf_areas.join(", ")}</div>
                    )}
                    {r.ppf_brand && <div className="text-[10px] text-slate-500 mt-0.5">Brand: {r.ppf_brand}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-300 max-w-[280px]">
                    {r.materials?.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {r.materials.slice(0, 4).map((m, i) => (
                            <span key={i} className="pill pill-muted text-[10px]">{m}</span>
                          ))}
                          {r.materials.length > 4 && (
                            <span className="text-[10px] text-slate-500">+{r.materials.length - 4} more</span>
                          )}
                        </div>
                      : <span className="text-slate-500 italic text-xs">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right text-white font-medium">
                    {r.price > 0 ? `$${r.price.toLocaleString()}` : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                    {r.invoiced_at ? new Date(r.invoiced_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
